import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ErrorHandler, Injector, OnChanges, OnDestroy, OnInit, inject, signal } from '@angular/core'
import { AbstractControl } from '@angular/forms'
import { FieldTree, applyEach, disabled, form, min, readonly, required } from '@angular/forms/signals'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'

import {
  Lasku,
  LaskuBase,
  LaskuReskontra,
  Laskuasetukset,
  LaskunTuote,
  LaskunTyypit,
  LaskunTyyppi,
  LaskunumeroTyyppi,
  LaskutettavatPalvelut,
  UUDEN_LASKUN_AVAIN,
} from '@lemon/core-firebase/model/lasku'
import { LaskuChangeNoopCollector, LaskuSharedService, ReskontraService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { TuettuKieli } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'

import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { LaskuPdfEsikatselutiedot } from '../_jaettu-angular/laskut/esikatselu/pdf.perinteinen.component'
import { laskunAsiakkaanSkeema } from '../_jaettu-angular/laskut/muokkaa/lasku-asiakkaan-tiedot.component'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { KonfiguraatioPalvelu } from '../_jaettu-angular/service/lasku/configuration.service'
import { VasenValikkoService } from '../_jaettu-angular/service/vasen-valikko.service'

import { LaskuComponentData, LaskuKatseleComponentDataResolve, LaskuKatseleComponentExistingData } from '../_angular/_resolvers/lasku.resolve'
import { AsiakasService } from '../_angular/service/asiakas/asiakas.service'
import { KirjanpitajaService } from '../_angular/service/kirjanpitaja/kirjanpitaja.service'
import { LaskuService } from '../_angular/service/lasku/lasku.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'
import { LaskuLataaDialog, LaskuLataaDialogData } from './dialogit/lasku.lataa.dialog'

import { Asiakas } from '../_jaettu-lemonator/model/asiakas'

import { LaskuSpostiData, LaskuSpostiDialog } from './dialogit/lasku.sposti.dialog'

import { ApixReceivedInvoiceConfig } from '@lemon/core-firebase/model/apix'
import { LaskuKorkoService } from '@lemon/core-firebase/service/lasku/lasku-korko.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { KirjanpitajaTiimiAvaimet, KirjanpitajanNimitiedot, KirjanpitajanTiimi } from 'app/_jaettu-lemonator/model/kirjanpitaja'
import { KirjanpitajanTiimiService } from 'app/_jaettu-lemonator/service/kirjanpitajan-tiimi.service'
import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest, firstValueFrom, from, of } from 'rxjs'
import { auditTime, map, switchMap, take, takeUntil } from 'rxjs/operators'
import { LEMONATOR_CF_API, LemonHttpService } from '../_angular/service/lemon-http.service'
import { LaskuLomake as JaettuLaskuLomake, LadattavaLiitetiedosto, laskunRivinSkeema, liitetiedostonSkeema } from './lasku-rivit.component'

interface LaskuLomake extends JaettuLaskuLomake {
  // Hallinnolliset
  tilastoKirjanpitaja: KirjanpitajanNimitiedot | null
  tilastoTiimi: KirjanpitajanTiimi | null
}

@Component({
  templateUrl: './lasku.component.html',
  styleUrls: ['./lasku.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LaskuComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  private readonly _salesTilasto = {
    tuotteet: [LaskutettavatPalvelut.HINNANKOROTUS_WINBACK, LaskutettavatPalvelut.KAMPANJAHYVITYS],
    kirjanpitajaAvain: '8a5xb1esZDMDrDFzkJNSa6WgLJ83', // Samuli Saarinen
    tiimiAvain: KirjanpitajaTiimiAvaimet.SALES,
  }

  private ngUnsubscribe = new Subject<void>()
  tilaaEsikatseluPaivitysSubject: ReplaySubject<number> = new ReplaySubject(1)

  laskunTuotteetObservable: ReplaySubject<LaskunTuote[]> = new ReplaySubject(1)
  laskunLiitetiedostotObservable: ReplaySubject<LadattavaLiitetiedosto[]> = new ReplaySubject(1)
  laskunTyyppiObservable: BehaviorSubject<LaskunTyyppi> = new BehaviorSubject(LaskunTyypit.TAVALLINEN)
  laskunVastaanottajanMaaObservable: BehaviorSubject<string> = new BehaviorSubject('FIN')
  naytaLaskunTyyppiObservable: BehaviorSubject<boolean> = new BehaviorSubject(true)
  laskuObservable: ReplaySubject<{ juurilasku: Lasku; kasiteltava: LaskuBase }> = new ReplaySubject(1)
  esikatselutiedot: ReplaySubject<LaskuPdfEsikatselutiedot> = new ReplaySubject(1)
  lahetaEmailinaObservable: Observable<boolean>
  lahetaSahkoisenaObservable: Observable<boolean>
  tiimitObservable: Observable<KirjanpitajanTiimi[]>
  kirjanpitajienNimiTiedotObservable: Observable<KirjanpitajanNimitiedot[]>

  private _apixForwardConfigObservable: Observable<ApixReceivedInvoiceConfig>
  paivitaValidaatiot = {}

  lomake: FieldTree<LaskuLomake>
  private readonly _injector = inject(Injector)
  private readonly _ennakkolaskuPoissa = signal(false)
  private readonly _tyyppiPoissa = signal(false)
  private readonly _tilastoPoissa = signal(false)

  juurilasku: Lasku = null
  kasiteltava: LaskuBase = null
  reskontra: LaskuReskontra[] = null
  asetukset: Laskuasetukset = null
  aiemminMaksettu = 0

  alvitonSumma = 0
  alvSumma = 0
  yhteensaSumma = 0

  hyvitys = false
  asetuksissaOnOletustietojaVirhe = false
  hyvitysPositiivinenVirhe = false
  alkuperainenNrotyyppi: LaskunumeroTyyppi = null

  private _buttonPushInflight: boolean = false

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private laskuService: LaskuService,
    private laskuKopioija: LaskuKopioija,
    private validationService: FormValidationService,
    private errorHandler: ErrorHandler,
    private laskuSharedService: LaskuSharedService,
    private vasenValikkoService: VasenValikkoService,
    private dialog: MatDialog,
    private currencyService: CurrencyService,
    private ladataanService: LadataanService,
    // private asetustenOletuksetService: AsetustenOletuksetService,
    // private snackbar: MatSnackBar,
    // private laskutResolve: LaskutComponentDataResolve,
    private translationService: LemonTranslationService,
    // private location: Location,
    private laskuKatseleComponentDataResolve: LaskuKatseleComponentDataResolve,
    private reskontraService: ReskontraService,
    private asiakasService: AsiakasService,
    private konfiguraatioPalvelu: KonfiguraatioPalvelu,
    private kirjanpitajaService: KirjanpitajaService,
    private _lemonaidFirebase: FirebaseLemonaid,
    private _korkoService: LaskuKorkoService,
    private _laskuUriService: LaskuUriService,
    private _kirjanpitajanTiimiService: KirjanpitajanTiimiService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _httpService: LemonHttpService,
    private _fileSaverService: FileSaverService,
  ) {
    this.juurilasku = this.laskuKopioija.annaUusiLaskuIlmanKorkoprosenttia()
    this.kasiteltava = this.juurilasku

    const laskunTyyppi: LaskunTyyppi = {
      avain: this.kasiteltava.tyyppi,
    }
    const kasiteltavaEiOleJuurilasku = this.kasiteltava.avain !== this.juurilasku.avain
    this._ennakkolaskuPoissa.set(kasiteltavaEiOleJuurilasku)
    this._tyyppiPoissa.set(kasiteltavaEiOleJuurilasku)
    const alkuarvo: LaskuLomake = {
      // Yläosan dropparit
      ennakkolasku: false,
      tyyppi: laskunTyyppi,
      kieli: this.kasiteltava.kieli ?? null,
      valuutta: this.kasiteltava.valuutta ?? null,

      // Asiakkaan tiedot
      nimi: null,
      ytunnus: '',
      katuosoite: '',
      maa: '',
      postinro: '',
      postitoimipaikka: '',
      ytunnuksenPakollisuus: null,
      maaPoissa: false,

      // Laskun muut tiedot
      laskunpvm: null,
      erapaiva: null,
      maksuaikaa: null,
      toimituspvm: null,
      lisatiedot: '',
      viitteenne: '',
      viivastyskorko: null,

      // Tuotteet
      tuotteet: [],
      liitetiedostot: [],

      tilastoKirjanpitaja: null,
      tilastoTiimi: null,
    }
    this.lomake = form(signal(alkuarvo), polku => {
      disabled(polku.ennakkolasku, () => this._ennakkolaskuPoissa())
      disabled(polku.tyyppi, () => this._tyyppiPoissa())

      laskunAsiakkaanSkeema(polku)
      required(polku.katuosoite)
      required(polku.maa)
      required(polku.postinro)
      required(polku.postitoimipaikka)

      disabled(polku.laskunpvm)
      readonly(polku.laskunpvm)
      required(polku.erapaiva)
      required(polku.maksuaikaa)
      min(polku.maksuaikaa, 1)
      required(polku.viivastyskorko)
      min(polku.viivastyskorko, 0.01)

      validoiReaktiivisesti(polku.tuotteet, this.vahintaanYksiRiviValidator)
      applyEach(polku.tuotteet, laskunRivinSkeema)
      applyEach(polku.liitetiedostot, liitetiedostonSkeema)

      required(polku.tilastoKirjanpitaja)
      disabled(polku.tilastoKirjanpitaja, () => this._tilastoPoissa())
      required(polku.tilastoTiimi)
      disabled(polku.tilastoTiimi, () => this._tilastoPoissa())
    })

    this.registerForChanges()
  }

  get lomakeJaettuna(): FieldTree<JaettuLaskuLomake> {
    return this.lomake as unknown as FieldTree<JaettuLaskuLomake>
  }

  vahintaanYksiRiviValidator = (c: AbstractControl<unknown[]>) => {
    if (c.value.length < 1) {
      return { min: 'true' }
    }
    return null
  }

  ngOnChanges() {
    // console.log('lasku changes')
  }

  ngOnInit() {
    arvonMuutokset(this.lomake.tuotteet, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe), auditTime(50))
      .subscribe(aika => {
        this.laskeSummat()
        this.asetaEnnakolaskuLaskusta(this.juurilasku, this.kasiteltava)
      })

    this.kirjanpitajienNimiTiedotObservable = this.kirjanpitajaService.kirjanpitajienNimitiedotObservable.pipe(
      map(kirjanpitajat =>
        kirjanpitajat
          .filter(k => {
            return (
              k.avain !== 'uekRGWgeLqecXk0Gh8el0s85SEt1' && // Taneli Testinen
              k.avain !== 'QgPvtcCjoOdf6Zg7lgMwqLWp2BG2'
            ) // Herra Holvi
          })
          .sort((a, b) => a.etunimi.localeCompare(b.etunimi) || a.sukunimi.localeCompare(b.sukunimi)),
      ),
    )

    this.tilaaEsikatseluPaivitysSubject.pipe(takeUntil(this.ngUnsubscribe), auditTime(250)).subscribe(aika => {
      // console.log('tilattu esikatselu paivitys: ', aika)
      if (this.juurilasku && this.kasiteltava && this.kasiteltava.asetukset) {
        this.esikatselutiedot.next({
          asetukset: this.kasiteltava.asetukset,
          juurilasku: this.juurilasku,
          kasiteltava: this.kasiteltava,
        })
      }
    })

    this._apixForwardConfigObservable = this.laskuObservable.pipe(
      switchMap(({ kasiteltava }) => {
        if (!kasiteltava?.asiakas?.avain) {
          return of<ApixReceivedInvoiceConfig>(null)
        }
        const uri = 'customers/' + kasiteltava.asiakas.avain + '/apix-received-invoice-config/' + kasiteltava.asiakas.avain
        return this._lemonaidFirebase.firestoreDoc<ApixReceivedInvoiceConfig>(uri).listen()
      }),
    )

    this.lahetaSahkoisenaObservable = this.laskuObservable.pipe(
      switchMap(({ kasiteltava }) => {
        if (!kasiteltava?.asiakas?.avain) {
          return of(false)
        }
        return combineLatest([from(this.laskuService.haeAsiakas(kasiteltava)), this._apixForwardConfigObservable]).pipe(
          map(([asiakas, sahkoisetValitystiedot]) => {
            if (sahkoisetValitystiedot && sahkoisetValitystiedot.forwardLemontreeLaskut && sahkoisetValitystiedot.paymentReceiveIsActive) {
              // Jos maksut on asiakkaalla käytössä -> menee aina maksuihin.
              return true
            } else if (sahkoisetValitystiedot && sahkoisetValitystiedot.forwardLemontreeLaskut && sahkoisetValitystiedot.forwardActive) {
              if (!sahkoisetValitystiedot.forwardAddress || !sahkoisetValitystiedot.forwardAddress.sahkoinenOsoite || !sahkoisetValitystiedot.forwardAddress.sahkoinenValittaja) {
                throw new Error('Asiakkaalle asiakkaat/' + asiakas.avain + ' on merkitty välitys aktiiviseksi ja lemontreen laskujen välitys päälle, mutta sähköistä osoitetta ei löydy.')
              }
              return true
            } else if (asiakas?.sahkoinenLaskutusosoite && asiakas?.sahkoinenLaskutusosoite.sahkoinenOsoite && asiakas?.sahkoinenLaskutusosoite.sahkoinenValittaja) {
              return true
            }
            return false
          }),
        )
      }),
    )

    this.lahetaEmailinaObservable = this.lahetaSahkoisenaObservable.pipe(map(lahetaSahkoisena => !lahetaSahkoisena))

    this.tiimitObservable = this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(
      switchMap(k => {
        return this._kirjanpitajanTiimiService.annaKaikkiTiimit()
      }),
    )

    combineLatest([this.route.data, this.asiakasService.nykyinenAsiakasObservable])
      .pipe(
        switchMap(([data, asiakas]: [{ data: LaskuComponentData }, Asiakas]) => {
          if (asiakas?.avain && data?.data?.juurilasku?.avain) {
            const laskuReskontraUri = this._laskuUriService.getCustomerLaskunReskontraCollectionUri(asiakas.avain, data.data.juurilasku.avain)
            return this._lemonaidFirebase
              .firestoreCollection<LaskuReskontra>(laskuReskontraUri)
              .listen()
              .pipe(
                map(reskontra => {
                  return [data, asiakas, reskontra]
                }),
              )
          }
          return of([null, null, null])
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe({
        next: async ([data, asiakas, reskontra]: [{ data: LaskuComponentData }, Asiakas, LaskuReskontra[]]) => {
          if (data && asiakas && reskontra) {
            await this.alustaNakymaKokonaan(asiakas, data.data.asetukset, data.data.juurilasku, data.data.kasiteltava, reskontra)
          }
        },
        error: error => {
          this.errorHandler.handleError(error)
        },
      })

    setTimeout(() => {
      this.vasenValikkoService.paivitaAuki(false)
    }, 100)

    window.scroll({ top: 0, left: 0 })
  }

  private async alustaNakymaKokonaan(asiakas: Asiakas, asetukset: Laskuasetukset, juurilasku: Lasku, kasiteltava: LaskuBase, reskontra: LaskuReskontra[]) {
    if (!asiakas || !reskontra) {
      return
    }

    this.asetukset = asetukset
    this.juurilasku = juurilasku
    this.kasiteltava = kasiteltava
    this.kasiteltava.asetukset = asetukset
    this.reskontra = reskontra

    this.laskuSharedService.paivitaRyppaanJaKasiteltavanSummat(juurilasku, kasiteltava, reskontra, new LaskuChangeNoopCollector())

    if (!this.kasiteltava.tuotteet) {
      this.kasiteltava.tuotteet = []
    }

    if (!this.kasiteltava.liitteet) {
      this.kasiteltava.liitteet = []
    }

    if (this.juurilasku.viivastyskorkoprosentti === undefined || this.juurilasku.viivastyskorkoprosentti === null) {
      this.juurilasku.viivastyskorkoprosentti = await this._korkoService.annaOletusViivastyskorkoProsenttiNumber()
    }

    this.alkuperainenNrotyyppi = kasiteltava.nrotyyppi

    if (this.juurilasku.avain === UUDEN_LASKUN_AVAIN) {
      const kieli = asiakas.laskunKieli || 'fi'
      this.juurilasku.asiakas.avain = asiakas.avain

      // Osoitetiedot
      this.juurilasku.asiakas.nimi = asiakas.nimi
      this.juurilasku.asiakas.ytunnus = asiakas.ytunnus
      this.juurilasku.asiakas.katuosoite = asiakas.katuosoite
      this.juurilasku.asiakas.postinro = asiakas.postinro
      this.juurilasku.asiakas.postitmp = asiakas.postitmp
      this.juurilasku.asiakas.maa = asiakas.maa

      // Email tiedot
      this.juurilasku.asiakas.laskunKieli = kieli
      this.juurilasku.asiakas.laskunTyyppi = asiakas.laskunTyyppi || LaskunTyypit.TAVALLINEN.avain
      this.juurilasku.asiakas.laskunValuutta = asiakas.laskunValuutta || 'EUR'
      this.juurilasku.asiakas.laskunVastaanottajat = asiakas.laskunVastaanottajat || []

      this.juurilasku.kieli = kieli
      this.kasiteltava.kieli = kieli

      // ERILLISLASKUTUS MENEE AINA ERI VIITENUMEROLLA
      // this.juurilasku.viitenumero = asiakas.viitenumero
    }

    this.laskunTuotteetObservable.next(this.kasiteltava.tuotteet)
    this.laskuObservable.next({ juurilasku: this.juurilasku, kasiteltava: this.kasiteltava })

    // NOTE! THE EXACT ARRAY INSTANCE FROM KASITELTAVA MUST BE PLACED TO THIS
    // ARRAY! IT IS UGLY UGLY, BUT WORKS!
    // NO MAP, NO FILTER, NO NOTHING!
    // The reason for is that the sub component directly modifies the array, which is
    // inside the lasku instance. If we map of filter the array, a new array is created, and
    // the changes are not visible inside the lasku anymore.
    //
    // Loop below marks all already uploaded liitteet as done.
    // The data in lasku is cleaned when saving by the lasku.kopioija, which doesn't identify these
    // properties. (and hence leaves those out of the created copy)
    for (const liite of this.kasiteltava.liitteet) {
      const asLadattava = liite as LadattavaLiitetiedosto
      asLadattava.uploadDone = true
      asLadattava.uploadDonePercentage = 100
    }
    this.laskunLiitetiedostotObservable.next(this.kasiteltava.liitteet)

    if (this.kasiteltava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
      const summat = this.laskuSharedService.annaLaskunSummatJaTilaLaskulle(this.juurilasku, reskontra)
      const aiemminMaksettuTaiHyvitetty = summat.reskontrasumma.plus(summat.hyvitykset.abs())
      this.aiemminMaksettu = 0 - this.currencyService.muutaBigDecimalRahaksi(aiemminMaksettuTaiHyvitetty)
    } else {
      this.aiemminMaksettu = 0
    }

    if (this.kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
      this.hyvitys = true
    } else {
      this.hyvitys = false
    }

    const reskontrasumma = this.reskontraService.annaReskontranSumma(reskontra)
    this.aiemminMaksettu = 0 - this.currencyService.muutaBigDecimalRahaksi(reskontrasumma)

    // Hae laskunumero
    if (this.kasiteltava.lukossa) {
      // const muotoiltu = this.laskuSharedService.annaMuotoiltuLaskunumero(this.kasiteltava)
      // this.laskuForm.get('laskunro').setValue(muotoiltu)
    } else {
      // Don't block this, might take time.
      this.laskuService.haeLaskunumeroArvio(this.kasiteltava).then(numero => {
        // console.log('TÄÄLLÄ!!', numero)
        this.kasiteltava.nro = numero
        this.tilaaEsikatseluPaivitysSubject.next(1)
        // const muotoiltu = this.laskuSharedService.annaMuotoiltuLaskunumero(this.kasiteltava)
        // this.laskuForm.get('laskunro').setValue(muotoiltu)
      })
    }

    // this.laskuForm.get('tyyppi').setValue(this.kasiteltava.tyyppi)
    asetaArvo(this.lomake.kieli, this.kasiteltava.kieli ?? null)
    asetaArvo(this.lomake.valuutta, this.kasiteltava.valuutta ?? null)

    await this.asetaTilastoTiedot(this.kasiteltava)

    await this.asetaTyyppiLaskusta(this.kasiteltava)

    this.laskeSummat()

    this.asetaEnnakolaskuLaskusta(this.juurilasku, this.kasiteltava)
  }

  ngAfterViewInit() {}

  compareTiimit(tiimi1: KirjanpitajanTiimi, tiimi2: KirjanpitajanTiimi) {
    return tiimi1?.avain === tiimi2?.avain
  }

  compareKirjanpitajat(kp1: KirjanpitajanNimitiedot, kp2: KirjanpitajanNimitiedot) {
    return kp1?.avain === kp2?.avain
  }

  private async asetaTilastoTiedot(kasiteltava: LaskuBase) {
    // Varmista, että on tyhjä objekti
    if (!kasiteltava.tilastot) {
      kasiteltava.tilastot = { kirjanpitaja: null, tiimi: null }
    }

    const onkoSalesTuote = this.onkoSalesTuoteLaskulla()
    if (onkoSalesTuote) {
      await this.lukitseTilastoSalesiin()
      return
    }

    // Asetetaan aina kirjautunut kirjanpitäjä
    const kirjautunutKirjanpitaja = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
    kasiteltava.tilastot.kirjanpitaja = kirjautunutKirjanpitaja.uid

    // Aseta laskuun tiimi
    const kaikkiTiimit = await firstValueFrom(this.tiimitObservable)
    const tiimi = kaikkiTiimit.find(
      suspect =>
        suspect.esihenkilot.some(e => e.kirjanpitajaAvain === kasiteltava.tilastot.kirjanpitaja && e.primary) ||
        suspect.jasenet.some(j => j.kirjanpitajaAvain === kasiteltava.tilastot.kirjanpitaja && j.primary),
    )
    if (tiimi) {
      kasiteltava.tilastot.tiimi = tiimi.avain
    }

    // Alusta form laskun kirjanpitäjällä
    if (kasiteltava.tilastot.kirjanpitaja) {
      const kirjanpitajienNimitiedot = await firstValueFrom(this.kirjanpitajienNimiTiedotObservable)
      const kirjanpitaja = kirjanpitajienNimitiedot.find(k => k.avain === kasiteltava.tilastot.kirjanpitaja)
      if (kirjanpitaja) {
        asetaArvo(this.lomake.tilastoKirjanpitaja, kirjanpitaja)
      }
    }

    // Alusta form laskun tiimillä
    if (kasiteltava.tilastot.tiimi) {
      const team = kaikkiTiimit.find(t => t.avain === kasiteltava.tilastot.tiimi)
      if (team) {
        asetaArvo(this.lomake.tilastoTiimi, team)
      }
    }
  }

  private onkoSalesTuoteLaskulla(): boolean {
    return (this.kasiteltava.tuotteet ?? []).some(tuote => this._salesTilasto.tuotteet.includes(tuote.tuote?.avain as LaskutettavatPalvelut))
  }

  private async tarkistaSalesLukitus() {
    const salesTuoteLaskulla = this.onkoSalesTuoteLaskulla()
    const lukittu = this.lomake.tilastoKirjanpitaja().disabled()
    if (salesTuoteLaskulla === lukittu) {
      return
    }
    if (salesTuoteLaskulla) {
      await this.lukitseTilastoSalesiin()
    } else {
      this._tilastoPoissa.set(false)
      await this.asetaTilastoTiedot(this.kasiteltava)
    }
  }

  private async lukitseTilastoSalesiin() {
    if (!this.kasiteltava.tilastot) {
      this.kasiteltava.tilastot = { kirjanpitaja: null, tiimi: null }
    }
    this.kasiteltava.tilastot.kirjanpitaja = this._salesTilasto.kirjanpitajaAvain
    this.kasiteltava.tilastot.tiimi = this._salesTilasto.tiimiAvain

    const kirjanpitajienNimitiedot = await firstValueFrom(this.kirjanpitajienNimiTiedotObservable)
    const kirjanpitaja = kirjanpitajienNimitiedot.find(k => k.avain === this._salesTilasto.kirjanpitajaAvain)
    if (kirjanpitaja) {
      asetaArvo(this.lomake.tilastoKirjanpitaja, kirjanpitaja)
    }

    const kaikkiTiimit = await firstValueFrom(this.tiimitObservable)
    const tiimi = kaikkiTiimit.find(t => t.avain === this._salesTilasto.tiimiAvain)
    if (tiimi) {
      asetaArvo(this.lomake.tilastoTiimi, tiimi)
    }

    this._tilastoPoissa.set(true)
  }

  asetaEnnakolaskuLaskusta(juurilasku: Lasku, kasiteltava: LaskuBase) {
    if (kasiteltava.nrotyyppi !== LaskunumeroTyyppi.TAVALLINEN || juurilasku.avain !== kasiteltava.avain) {
      if (this.naytaLaskunTyyppiObservable.value !== false) {
        this.naytaLaskunTyyppiObservable.next(false)
      }
      asetaArvo(this.lomake.ennakkolasku, false)
      this._ennakkolaskuPoissa.set(true)
      return
    }
    if (this.naytaLaskunTyyppiObservable.value !== true) {
      this.naytaLaskunTyyppiObservable.next(true)
    }
    asetaArvo(this.lomake.ennakkolasku, !!juurilasku.ennakkolasku)
    this._ennakkolaskuPoissa.set(false)
  }

  async asetaTyyppiLaskusta(kasiteltava: LaskuBase) {
    // // Set the existing type
    if (kasiteltava.tyyppi != null) {
      const tyypit = await firstValueFrom(this.konfiguraatioPalvelu.tyypitObservable)
      for (const tyyppi of tyypit) {
        if (kasiteltava.tyyppi === tyyppi.avain) {
          asetaArvo(this.lomake.tyyppi, tyyppi)
          break
        }
      }
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  private laskeSummat() {
    const summat = this.laskuSharedService.annaLaskunSummat(this.kasiteltava)
    this.alvitonSumma = this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaIlmanAlv)
    this.alvSumma = this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaAlv)
    this.yhteensaSumma = this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaKaikki)
    if (this._annaYhteensaSentteina() < 0) {
      this.hyvitysPositiivinenVirhe = false
      this.kasiteltava.nrotyyppi = LaskunumeroTyyppi.HYVITYS
    } else {
      this.kasiteltava.nrotyyppi = this.alkuperainenNrotyyppi
    }
    this.tilaaEsikatseluPaivitysSubject.next(1)
    this._changeDetectorRef.markForCheck()
  }

  registerForChanges(): void {
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    muutokset(this.lomake).subscribe(() => {
      this.tilaaEsikatseluPaivitysSubject.next(1)
    })

    muutokset(this.lomake.maa)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(maa => {
        if (this.laskunVastaanottajanMaaObservable.value !== maa) {
          // console.log('maa muuttui', maa, '!==', this.laskunVastaanottajanMaaObservable.value)
          this.laskunVastaanottajanMaaObservable.next(maa)
        }
      })

    muutokset(this.lomake.ennakkolasku)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: boolean) => {
        if (value !== null && value !== undefined) {
          if (this.kasiteltava.nrotyyppi !== LaskunumeroTyyppi.TAVALLINEN || this.kasiteltava.avain !== this.juurilasku.avain) {
            delete this.juurilasku.ennakkolasku
          } else {
            this.juurilasku.ennakkolasku = !!value
          }
          this.laskuObservable.next({ juurilasku: this.juurilasku, kasiteltava: this.kasiteltava })
        }
      })

    muutokset(this.lomake.tyyppi)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: LaskunTyyppi) => {
        if (value) {
          if (this.kasiteltava.tyyppi !== value.avain) {
            // console.log('tyyppi muuttui', value?.avain, '!==', this.laskunTyyppiObservable.value?.avain)
            this.kasiteltava.tyyppi = value.avain
            this.laskunTyyppiObservable.next(value)
            this.laskuObservable.next({ juurilasku: this.juurilasku, kasiteltava: this.kasiteltava })
          }
        }
      })

    muutokset(this.lomake.kieli)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (value: TuettuKieli) => {
        if (value) {
          this.kasiteltava.kieli = value
        }
      })

    muutokset(this.lomake.valuutta)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: string) => {
        if (value) {
          this.kasiteltava.valuutta = value
        }
      })

    muutokset(this.lomake.tilastoKirjanpitaja)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: KirjanpitajanNimitiedot) => {
        if (!this.kasiteltava.tilastot) {
          this.kasiteltava.tilastot = { kirjanpitaja: null, tiimi: null }
        }
        this.kasiteltava.tilastot.kirjanpitaja = value?.avain ?? null
      })

    muutokset(this.lomake.tilastoTiimi)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: KirjanpitajanTiimi) => {
        if (!this.kasiteltava.tilastot) {
          this.kasiteltava.tilastot = { kirjanpitaja: null, tiimi: null }
        }
        this.kasiteltava.tilastot.tiimi = value?.avain ?? null
      })

    muutokset(this.lomake.tuotteet)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.tarkistaSalesLukitus()
      })
  }

  paivitaValittuAsiakas() {
    if (!this.kasiteltava.asiakas || !this.kasiteltava.asiakas.avain) {
      return
    }

    const asiakas = this.kasiteltava.asiakas

    // Aseta laskun tyyppi
    if (asiakas.laskunTyyppi) {
      this.kasiteltava.tyyppi = asiakas.laskunTyyppi
      this.asetaTyyppiLaskusta(this.kasiteltava)
    }

    // Aseta laskun valuutta
    if (asiakas.laskunValuutta) {
      this.kasiteltava.valuutta = asiakas.laskunValuutta
      asetaArvo(this.lomake.valuutta, this.kasiteltava.valuutta)
    }

    // Aseta laskun kieli
    if (asiakas.laskunKieli) {
      this.kasiteltava.kieli = asiakas.laskunKieli
      asetaArvo(this.lomake.kieli, this.kasiteltava.kieli)
    }

    // Päivitä kaikki.
    this.laskuObservable.next({ juurilasku: this.juurilasku, kasiteltava: this.kasiteltava })
  }

  private _annaYhteensaSentteina() {
    return Math.round((this.yhteensaSumma + this.aiemminMaksettu) * 100)
  }

  private tarkistaOnkoTiedotOikein(): boolean {
    if (this._annaYhteensaSentteina() >= 0 && this.hyvitys) {
      this.hyvitysPositiivinenVirhe = true
      return false
    } else {
      this.hyvitysPositiivinenVirhe = false
    }
    const virheitaLomakkeella = this.validationService.onkoLomakkeessaVirheita(this.lomake)
    if (!virheitaLomakkeella) {
      return true
    }
    this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
    this.paivitaValidaatiot = {}
    return false
  }

  // async save() {
  //   this.ladataanService.aloitaLataaminen()
  //   this.laskuService.tallennaLuonnos(this.juurilasku, this.kasiteltava).then(result => {
  //     const lokalisoitu = this.translationService.lokalisoi('lasku.tallennettu-onnistuneesti')
  //     this.snackbar.open(lokalisoitu, 'OK', { duration: 5000, verticalPosition: 'top' })
  //     this.location.replaceState('/laskutus/laskut/' + this.juurilasku.avain + '/' + this.kasiteltava.avain)
  //     this.alustaNakymaKokonaan(this.asiakas, this.asetukset, this.juurilasku, this.kasiteltava)
  //     this.ladataanService.lopetaLataaminen()
  //   }).catch(err => {
  //     this.ladataanService.lopetaLataaminen()
  //     this.errorHandler.handleError(err)
  //   })
  // }

  async saveAndSendElectronic() {
    if (!this.tarkistaOnkoTiedotOikein()) {
      return
    }

    this.ladataanService.aloitaLataaminen()
    const [kirjanpitaja, sahkoisetValitystiedot] = await firstValueFrom(combineLatest([this.kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable, this._apixForwardConfigObservable]))

    let asiakas = await firstValueFrom(this.asiakasService.nykyinenAsiakasObservable)

    if (this.kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
      asiakas = await this.laskuService.haeAsiakas(this.kasiteltava)
    }

    if (sahkoisetValitystiedot && sahkoisetValitystiedot.forwardLemontreeLaskut && sahkoisetValitystiedot.paymentReceiveIsActive) {
      // Jos maksut on asiakkaalla käytössä -> menee aina maksuihin.
      // Tallentaa laskun siis lemontree -yritykselle JA lisää sen
      // tulleisiin sähköisiin laskuihin ilman APIX:in läpi lähettämistä.
      await this.laskuService
        .merkitseLaskuLahetetyksiTulostamallaToisenHyvaksyntajonoon(asiakas, sahkoisetValitystiedot.receiveAddress, kirjanpitaja, this.juurilasku, this.kasiteltava)
        .then(() => {
          this.ladataanService.lopetaLataaminen()
          this.peruuta()
        })
        .catch(err => {
          this.ladataanService.lopetaLataaminen()
          this.errorHandler.handleError(err)
        })
    } else if (sahkoisetValitystiedot && sahkoisetValitystiedot.forwardLemontreeLaskut && sahkoisetValitystiedot.forwardActive) {
      if (!sahkoisetValitystiedot.forwardAddress || !sahkoisetValitystiedot.forwardAddress.sahkoinenOsoite || !sahkoisetValitystiedot.forwardAddress.sahkoinenValittaja) {
        throw new Error('Asiakkaalle asiakkaat/' + asiakas.avain + ' on merkitty välitys aktiiviseksi ja lemontreen laskujen välitys päälle, mutta sähköistä osoitetta ei löydy.')
      }
      if (sahkoisetValitystiedot.forwardRequiresApproving) {
        await this.laskuService
          .merkitseLaskuLahetetyksiTulostamallaToisenHyvaksyntajonoon(asiakas, sahkoisetValitystiedot.forwardAddress, kirjanpitaja, this.juurilasku, this.kasiteltava)
          .then(() => {
            this.ladataanService.lopetaLataaminen()
            this.peruuta()
          })
          .catch(err => {
            this.ladataanService.lopetaLataaminen()
            this.errorHandler.handleError(err)
          })
      } else {
        await this.laskuService
          .merkitseLaskuLahetetyksiSahkoisesti(asiakas, sahkoisetValitystiedot.forwardAddress, kirjanpitaja, this.juurilasku, this.kasiteltava)
          .then(() => {
            this.ladataanService.lopetaLataaminen()
            this.peruuta()
          })
          .catch(err => {
            this.ladataanService.lopetaLataaminen()
            this.errorHandler.handleError(err)
          })
      }
    } else if (asiakas.sahkoinenLaskutusosoite && asiakas.sahkoinenLaskutusosoite.sahkoinenOsoite && asiakas.sahkoinenLaskutusosoite.sahkoinenValittaja) {
      await this.laskuService
        .merkitseLaskuLahetetyksiSahkoisesti(asiakas, asiakas.sahkoinenLaskutusosoite, kirjanpitaja, this.juurilasku, this.kasiteltava)
        .then(() => {
          this.ladataanService.lopetaLataaminen()
          this.peruuta()
        })
        .catch(err => {
          this.ladataanService.lopetaLataaminen()
          this.errorHandler.handleError(err)
        })
    } else {
      throw new Error('Trying to send electronic invoice without electronic invoice address.')
    }
  }

  async saveAndSendEmail() {
    if (!this.tarkistaOnkoTiedotOikein()) {
      return
    }

    // TODO: MODAA KUN https://github.com/angular/material2/issues/8493 on korjattu
    // PULL JOKA KORJAA: https://github.com/angular/material2/pull/9236
    const kaytettavaLeveys = window.innerWidth
    const kaytettavaKorkeus = window.innerHeight
    const leveys = kaytettavaLeveys > 900 ? '90vw' : '98vw'
    const korkeus = kaytettavaKorkeus > 1100 ? '80vh' : kaytettavaKorkeus > 750 ? '85vh' : '92vh'

    combineLatest([this.asiakasService.nykyinenAsiakasObservable, this.kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable])
      .pipe(take(1), takeUntil(this.ngUnsubscribe))
      .subscribe(async ([asiakas, kirjanpitaja]) => {
        const data: LaskuSpostiData = {
          asiakas: asiakas,
          kirjanpitaja: kirjanpitaja,
          juurilasku: this.juurilasku,
          asetukset: this.asetukset,
          kasiteltava: this.kasiteltava,
        }

        if (this.kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
          data.asiakas = await this.laskuService.haeAsiakas(this.kasiteltava)
        }

        const settings: MatDialogConfig = {
          data: data,
          panelClass: 'ilman-paddingia',
          maxWidth: '1600px',
          maxHeight: korkeus,
          width: leveys,
        }

        const dialogRef = this.dialog.open(LaskuSpostiDialog, settings)
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.peruuta()
          }
        })
      })
  }

  async saveAndPrint() {
    if (this.kasiteltava.nrotyyppi !== LaskunumeroTyyppi.HYVITYS) {
      return
    }
    if (this.tarkistaOnkoTiedotOikein()) {
      if (this._buttonPushInflight) {
        return
      }
      this._buttonPushInflight = true
      this.ladataanService.aloitaLataaminen()

      const nykyinenAsiakas = await firstValueFrom(this.asiakasService.nykyinenAsiakasObservable)
      if (!nykyinenAsiakas) {
        return
      }

      const asiakasLaskulla = await this.laskuService.haeAsiakas(this.kasiteltava)
      const kirjanpitaja = await firstValueFrom(this.kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable)

      this.laskuService
        .merkitseLaskuLahetetyksiTulostamalla(asiakasLaskulla, kirjanpitaja, this.juurilasku, this.kasiteltava)
        .then(result => {
          this.ladataanService.lopetaLataaminen()
          const data: LaskuKatseleComponentExistingData = {
            juurilasku: this.juurilasku,
            kasiteltava: this.kasiteltava,
          }

          this.laskuKatseleComponentDataResolve.asetaOlemassaolevaData(data)

          if (nykyinenAsiakas.asiakasId === this.laskuService.LEMONTREE_ASIAKAS_ID) {
            this.router.navigate(['asiakkaat', nykyinenAsiakas.avain, 'laskut', 'selaa', this.juurilasku.avain, this.kasiteltava.avain]).then(done => {
              setTimeout(() => {
                this.lataaLasku()
                this._changeDetectorRef.markForCheck()
              }, 25)
            })
          } else {
            this.router.navigate(['asiakkaat', nykyinenAsiakas.avain, 'laskutus']).then(done => {
              setTimeout(() => {
                this.lataaLasku()
                this._changeDetectorRef.markForCheck()
              }, 25)
            })
          }
        })
        .catch(err => {
          this.ladataanService.lopetaLataaminen()
          this.errorHandler.handleError(err)
        })
        .finally(() => {
          this._buttonPushInflight = false
        })
    }
  }

  async lataaLasku() {
    const asiakas = await firstValueFrom(this.asiakasService.nykyinenAsiakasObservable)
    if (!asiakas?.avain) {
      return
    }
    if ((this.kasiteltava.print && this.kasiteltava.print.done) || (this.kasiteltava.email && this.kasiteltava.email.done) || (this.kasiteltava.sahkoinen && this.kasiteltava.sahkoinen.done)) {
      this.ladataanService.aloitaLataaminen()
      const pdfUri = this.laskuService.getPdfUrlAsiakkaalle(asiakas.asiakasId + '', this.kasiteltava)
      const url = '/api/1/laskut/lataaPdf/' + pdfUri
      return this._httpService
        .getBinary('/laskuLataaPdf?a=' + encodeURIComponent(url) + '&time=' + encodeURIComponent(new Date().getTime()), LEMONATOR_CF_API)
        .then(result => {
          const nimi = this.laskuService.annaPdfTiedostonNimiAsiakkaalle(asiakas, this.juurilasku, this.kasiteltava, this.translationService.nykyinenKieli)
          this.ladataanService.lopetaLataaminen()
          this._fileSaverService.saveAs(result, nimi)
        })
        .catch(err => {
          this.ladataanService.lopetaLataaminen()
          this.errorHandler.handleError(err)
        })
    } else {
      const data: LaskuLataaDialogData = {
        juurilasku: this.juurilasku,
        kasiteltava: this.kasiteltava,
      }
      const dialogRef = this.dialog.open(LaskuLataaDialog, { data: data, panelClass: 'ilman-paddingia' })
    }
  }

  peruuta() {
    this.asiakasService.nykyinenAsiakasAvainObservable.pipe(take(1)).subscribe(asiakas => {
      // Hallinto
      if (asiakas.avain === this.laskuService.LEMONTREE_ASIAKAS_AVAIN) {
        if (this.kasiteltava.avain === UUDEN_LASKUN_AVAIN) {
          this.router.navigate(['asiakkaat', asiakas.avain, 'laskut', 'selaa', this.juurilasku.avain, this.juurilasku.avain]).then(done => {
            this.vasenValikkoService.paivitaAuki(true)
          })
        } else {
          this.router.navigate(['asiakkaat', asiakas.avain, 'laskut', 'selaa', this.juurilasku.avain, this.kasiteltava.avain]).then(done => {
            this.vasenValikkoService.paivitaAuki(true)
          })
        }
        // Asiakkaan alla
      } else if (asiakas) {
        this.router.navigate(['asiakkaat', asiakas.avain, 'laskutus']).then(done => {
          this.vasenValikkoService.paivitaAuki(true)
        })
      } else {
        this.router.navigate(['asiakkaat']).then(done => {
          this.vasenValikkoService.paivitaAuki(true)
        })
      }
    })
  }
}
